import EventEmitter from 'events';

/**
 * Centralized Device Manager for Audio Devices
 * Handles automatic device detection, switching, and consistent device access across the application
 * 
 * Features:
 * - Automatic device switching when devices are connected/disconnected
 * - Fallback to system default when preferred devices are disconnected
 * - User preference for explicit device selection
 * - Persistent device state across app states (focus, minimize, etc.)
 * - Clean API for other components to access audio devices
 */
class DeviceManager extends EventEmitter {
  constructor() {
    super();

    // Device state
    this.currentInputDevice = null;
    this.currentOutputDevice = null;
    this.systemDefaultInputDevice = null;
    this.systemDefaultOutputDevice = null;
    this.userSelectedInputDevice = null;
    this.userSelectedOutputDevice = null;

    // Device lists
    this.availableInputDevices = [];
    this.availableOutputDevices = [];

    // Audio stream and context
    this.audioContext = null;
    this.audioStream = null;
    this.analyser = null;

    // State tracking
    this.isInitialized = false;
    this.isInitializing = false;
    this.deviceChangeListeners = [];
    this.isHandlingDeviceChange = false;

    // Configuration
    this.config = {
      autoSwitchDevices: true,
      preferHeadsetDevices: true,
      fallbackToDefault: true,
      deviceChangeDebounceMs: 200
    };

    // Bind methods
    this.handleDeviceChange = this.handleDeviceChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleWindowBlur = this.handleWindowBlur.bind(this);
    this.handleWindowFocus = this.handleWindowFocus.bind(this);

    // Initialize when module loads
    this.initialize();
  }

  /**
   * Initialize the device manager
   */
  async initialize() {
    if (this.isInitialized || this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    try {
      // Create audio context
      await this.createAudioContext();

      // Get initial device list
      await this.refreshDeviceList();

      // Set initial devices
      await this.setInitialDevices();

      // Setup device change detection
      this.setupDeviceChangeDetection();

      // Setup global event listeners
      this.setupGlobalEventListeners();

      this.isInitialized = true;
      this.isInitializing = false;

      console.log('✅ DeviceManager initialized successfully');
      this.emit('initialized');

    } catch (error) {
      console.error('❌ DeviceManager initialization failed:', error);
      this.isInitializing = false;
      throw error;
    }
  }

  /**
   * Create audio context for processing
   */
  async createAudioContext() {
    try {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }
        return this.audioContext;
      }

      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 44100,
        latencyHint: 'interactive'
      });

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      console.log('🎧 AudioContext created');
      return this.audioContext;
    } catch (error) {
      console.error('❌ Failed to create AudioContext:', error);
      throw error;
    }
  }

  /**
   * Refresh the list of available devices
   */
  async refreshDeviceList() {
    try {
      // Browser compatibility check
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.warn('🔧 Device enumeration not supported');
        return;
      }

      // Explicitly request permission if in browser and no devices found
      // This triggers the browser permission prompt
      if (!window.electronAPI && this.availableInputDevices.length === 0) {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e) {
          console.log("Browser permission request result:", e);
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      this.availableInputDevices = devices.filter(device => device.kind === 'audioinput');
      this.availableOutputDevices = devices.filter(device => device.kind === 'audiooutput');

      // Find system defaults
      this.systemDefaultInputDevice = this.availableInputDevices.find(device => device.deviceId === 'default') ||
        this.availableInputDevices[0] || null;
      this.systemDefaultOutputDevice = this.availableOutputDevices.find(device => device.deviceId === 'default') ||
        this.availableOutputDevices[0] || null;

      console.log('🔍 Available devices refreshed:', {
        inputDevices: this.availableInputDevices.length,
        outputDevices: this.availableOutputDevices.length
      });

      this.emit('devicesUpdated', {
        inputDevices: this.availableInputDevices,
        outputDevices: this.availableOutputDevices
      });

    } catch (error) {
      console.error('❌ Failed to refresh device list:', error);
    }
  }

  /**
   * Set initial devices based on system defaults or user preferences
   */
  async setInitialDevices() {
    try {
      // Use user selected device if available, otherwise system default
      const inputDevice = this.userSelectedInputDevice || this.systemDefaultInputDevice;
      const outputDevice = this.userSelectedOutputDevice || this.systemDefaultOutputDevice;

      if (inputDevice) {
        await this.setInputDevice(inputDevice.deviceId);
      }

      if (outputDevice) {
        await this.setOutputDevice(outputDevice.deviceId);
      }

    } catch (error) {
      console.error('❌ Failed to set initial devices:', error);
    }
  }

  /**
   * Setup device change detection
   */
  setupDeviceChangeDetection() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) {
      console.warn('🔧 Device change detection not supported');
      return;
    }

    navigator.mediaDevices.addEventListener('devicechange', this.handleDeviceChange);
    console.log('🔧 Device change detection enabled');
  }

  /**
   * Setup global event listeners
   */
  setupGlobalEventListeners() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('focus', this.handleWindowFocus);
  }

  /**
   * Handle device change events
   */
  async handleDeviceChange() {
    if (this.isHandlingDeviceChange) {
      return;
    }

    this.isHandlingDeviceChange = true;

    try {
      console.log('🔧 Device change detected, refreshing device list...');

      // Refresh device list
      await this.refreshDeviceList();

      // Handle automatic device switching if enabled, prioritizing new devices
      if (this.config.autoSwitchDevices) {
        await this.handleAutomaticDeviceSwitching();
      }

      this.emit('deviceChange', {
        inputDevices: this.availableInputDevices,
        outputDevices: this.availableOutputDevices
      });

    } catch (error) {
      console.error('❌ Error handling device change:', error);
    } finally {
      this.isHandlingDeviceChange = false;
    }
  }

  /**
   * Handle automatic device switching based on preferences
   */
  async handleAutomaticDeviceSwitching() {
    try {
      // Handle input device switching
      if (this.userSelectedInputDevice) {
        // If user has explicitly selected a device, don't auto-switch
        // But check if the selected device is still available
        const deviceStillAvailable = this.availableInputDevices.some(
          device => device.deviceId === this.userSelectedInputDevice.deviceId
        );

        if (!deviceStillAvailable) {
          console.log('🔌 User selected input device disconnected, falling back to system default');
          await this.setInputDevice('default');
        }
      } else {
        // Auto-select best available device, prioritizing newly connected devices
        const bestInputDevice = this.findBestInputDevice(true); // Pass true to prioritize new devices
        if (bestInputDevice && bestInputDevice.deviceId !== this.currentInputDevice?.deviceId) {
          console.log(`🔄 Auto-switching to best input device: ${bestInputDevice.label}`);
          await this.setInputDevice(bestInputDevice.deviceId);
        }
      }

      // Handle output device switching (similar logic)
      if (this.userSelectedOutputDevice) {
        const deviceStillAvailable = this.availableOutputDevices.some(
          device => device.deviceId === this.userSelectedOutputDevice.deviceId
        );

        if (!deviceStillAvailable) {
          console.log('🔌 User selected output device disconnected, falling back to system default');
          await this.setOutputDevice('default');
        }
      } else {
        // Auto-select best available device, prioritizing newly connected devices
        const bestOutputDevice = this.findBestOutputDevice(true); // Pass true to prioritize new devices
        if (bestOutputDevice && bestOutputDevice.deviceId !== this.currentOutputDevice?.deviceId) {
          console.log(`🔄 Auto-switching to best output device: ${bestOutputDevice.label}`);
          await this.setOutputDevice(bestOutputDevice.deviceId);
        }
      }

    } catch (error) {
      console.error('❌ Error during automatic device switching:', error);
    }
  }

  /**
   * Find the best input device based on preferences
   * @param {boolean} prioritizeNewDevices - Whether to prioritize newly connected devices
   */
  findBestInputDevice(prioritizeNewDevices = false) {
    if (this.availableInputDevices.length === 0) {
      return null;
    }

    // If we want to prioritize newly connected devices, check for recently connected ones
    if (prioritizeNewDevices && this.currentInputDevice) {
      // Look for devices that are not the current device but might be better
      const newDevices = this.availableInputDevices.filter(device =>
        device.deviceId !== this.currentInputDevice.deviceId &&
        device.deviceId !== 'default' &&
        device.deviceId !== 'communications'
      );

      // If we have new devices, prioritize headset devices among them
      if (newDevices.length > 0 && this.config.preferHeadsetDevices) {
        const newHeadsetDevices = newDevices.filter(device => {
          const label = device.label.toLowerCase();
          return label.includes('headset') ||
            label.includes('headphone') ||
            label.includes('earphone') ||
            label.includes('microphone') && !label.includes('array');
        });

        if (newHeadsetDevices.length > 0) {
          // Prefer the newest headset device (assuming it was added last)
          console.log(`🆕 Found ${newHeadsetDevices.length} new headset devices, selecting the latest one`);
          return newHeadsetDevices[newHeadsetDevices.length - 1];
        }

        // If no new headset devices, but we have new devices, prefer the latest one
        console.log(`🆕 Found ${newDevices.length} new devices, selecting the latest one`);
        return newDevices[newDevices.length - 1];
      }
    }

    // Standard device selection logic
    if (this.config.preferHeadsetDevices) {
      const headsetDevices = this.availableInputDevices.filter(device => {
        const label = device.label.toLowerCase();
        return label.includes('headset') ||
          label.includes('headphone') ||
          label.includes('earphone') ||
          label.includes('microphone') && !label.includes('array');
      });

      if (headsetDevices.length > 0) {
        return headsetDevices[0];
      }
    }

    // Return system default or first available device
    return this.systemDefaultInputDevice || this.availableInputDevices[0];
  }

  /**
   * Find the best output device based on preferences
   * @param {boolean} prioritizeNewDevices - Whether to prioritize newly connected devices
   */
  findBestOutputDevice(prioritizeNewDevices = false) {
    if (this.availableOutputDevices.length === 0) {
      return null;
    }

    // If we want to prioritize newly connected devices, check for recently connected ones
    if (prioritizeNewDevices && this.currentOutputDevice) {
      // Look for devices that are not the current device but might be better
      const newDevices = this.availableOutputDevices.filter(device =>
        device.deviceId !== this.currentOutputDevice.deviceId &&
        device.deviceId !== 'default' &&
        device.deviceId !== 'communications'
      );

      // If we have new devices, prioritize headset devices among them
      if (newDevices.length > 0 && this.config.preferHeadsetDevices) {
        const newHeadsetDevices = newDevices.filter(device => {
          const label = device.label.toLowerCase();
          return label.includes('headset') ||
            label.includes('headphone') ||
            label.includes('earphone') ||
            label.includes('speaker');
        });

        if (newHeadsetDevices.length > 0) {
          // Prefer the newest headset device (assuming it was added last)
          console.log(`🆕 Found ${newHeadsetDevices.length} new headset devices, selecting the latest one`);
          return newHeadsetDevices[newHeadsetDevices.length - 1];
        }

        // If no new headset devices, but we have new devices, prefer the latest one
        console.log(`🆕 Found ${newDevices.length} new devices, selecting the latest one`);
        return newDevices[newDevices.length - 1];
      }
    }

    // Standard device selection logic
    if (this.config.preferHeadsetDevices) {
      const headsetDevices = this.availableOutputDevices.filter(device => {
        const label = device.label.toLowerCase();
        return label.includes('headset') ||
          label.includes('headphone') ||
          label.includes('earphone') ||
          label.includes('speaker');
      });

      if (headsetDevices.length > 0) {
        return headsetDevices[0];
      }
    }

    // Return system default or first available device
    return this.systemDefaultOutputDevice || this.availableOutputDevices[0];
  }

  /**
   * Set input device by device ID
   */
  async setInputDevice(deviceId) {
    try {
      console.log(`🔄 Setting input device to: ${deviceId}`);

      // Stop existing stream if it exists
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
      }

      // Get new stream
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      };

      if (deviceId && deviceId !== 'default') {
        constraints.audio.deviceId = { exact: deviceId };
      } else {
        constraints.audio.deviceId = { exact: 'default' };
      }

      this.audioStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Get device info
      const track = this.audioStream.getAudioTracks()[0];
      const settings = track.getSettings();
      const deviceInfo = this.availableInputDevices.find(d => d.deviceId === settings.deviceId) ||
        { deviceId: settings.deviceId, label: track.label };

      // Update current device
      this.currentInputDevice = deviceInfo;
      this.userSelectedInputDevice = deviceId !== 'default' ? deviceInfo : null;

      // Setup audio processing
      await this.setupAudioProcessing();

      console.log(`✅ Input device set to: ${deviceInfo.label}`);
      this.emit('inputDeviceChanged', deviceInfo);

      // Also emit as window event for cross-component communication
      window.dispatchEvent(new CustomEvent('deviceSwitched', {
        detail: {
          type: 'input',
          device: deviceInfo,
          timestamp: Date.now()
        }
      }));

    } catch (error) {
      console.error('❌ Failed to set input device:', error);
      throw error;
    }
  }

  /**
   * Set output device by device ID
   */
  async setOutputDevice(deviceId) {
    try {
      console.log(`🔄 Setting output device to: ${deviceId}`);

      // Find device info
      const deviceInfo = this.availableOutputDevices.find(d => d.deviceId === deviceId) ||
        { deviceId, label: 'Unknown Output Device' };

      // Update current device
      this.currentOutputDevice = deviceInfo;
      this.userSelectedOutputDevice = deviceId !== 'default' ? deviceInfo : null;

      console.log(`✅ Output device set to: ${deviceInfo.label}`);
      this.emit('outputDeviceChanged', deviceInfo);

    } catch (error) {
      console.error('❌ Failed to set output device:', error);
      throw error;
    }
  }

  /**
   * Setup audio processing with analyser
   */
  async setupAudioProcessing() {
    try {
      if (!this.audioContext || !this.audioStream) {
        console.warn('⚠️ Cannot setup audio processing: missing audio context or stream');
        return;
      }

      // Create analyser if it doesn't exist
      if (!this.analyser) {
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.analyser.smoothingTimeConstant = 0.8;
      }

      // Create media stream source and connect to analyser
      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      source.connect(this.analyser);

      console.log('✅ Audio processing setup complete');

    } catch (error) {
      console.error('❌ Failed to setup audio processing:', error);
    }
  }

  /**
   * Get current audio stream
   */
  getAudioStream() {
    return this.audioStream;
  }

  /**
   * Get current audio context
   */
  getAudioContext() {
    return this.audioContext;
  }

  /**
   * Get analyser node for audio processing
   */
  getAnalyser() {
    return this.analyser;
  }

  /**
   * Get current input device
   */
  getCurrentInputDevice() {
    return this.currentInputDevice;
  }

  /**
   * Get current output device
   */
  getCurrentOutputDevice() {
    return this.currentOutputDevice;
  }

  /**
   * Get available input devices
   */
  getAvailableInputDevices() {
    return this.availableInputDevices;
  }

  /**
   * Get available output devices
   */
  getAvailableOutputDevices() {
    return this.availableOutputDevices;
  }

  /**
   * Get system default input device
   */
  getSystemDefaultInputDevice() {
    return this.systemDefaultInputDevice;
  }

  /**
   * Get system default output device
   */
  getSystemDefaultOutputDevice() {
    return this.systemDefaultOutputDevice;
  }

  /**
   * Handle visibility change (minimize, tab switch, etc.)
   */
  handleVisibilityChange() {
    console.log(`📱 Visibility changed: ${document.hidden ? 'hidden' : 'visible'}`);
    this.emit('visibilityChange', document.hidden);
  }

  /**
   * Handle window blur (app loses focus)
   */
  handleWindowBlur() {
    console.log('📱 Window lost focus');
    this.emit('windowFocus', false);
  }

  /**
   * Handle window focus (app gains focus)
   */
  handleWindowFocus() {
    console.log('📱 Window gained focus');
    this.emit('windowFocus', true);

    // Resume audio context if suspended
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    console.log('🧹 Cleaning up DeviceManager...');

    // Remove event listeners
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.handleDeviceChange);
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('focus', this.handleWindowFocus);

    // Stop audio stream
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clear references
    this.analyser = null;
    this.currentInputDevice = null;
    this.currentOutputDevice = null;
    this.userSelectedInputDevice = null;
    this.userSelectedOutputDevice = null;

    this.isInitialized = false;

    console.log('✅ DeviceManager cleanup complete');
  }
}

// Create global instance
export const deviceManager = new DeviceManager();

// Make it accessible from window for debugging
if (typeof window !== 'undefined') {
  window.deviceManager = deviceManager;
}